"use client"

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react"
import type { Locale } from "./translations"
import {
  getMessage,
  getStoredLocale,
  setStoredLocale,
} from "./translations"
import { useFirebaseAuth } from "@/lib/firebase-context"
import { updateUserPreferencesInFirestore } from "@/lib/firebase/firestore"

type LocaleOrNull = Locale | null

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
  /** Returns translation for key, or fallback when key is missing (e.g. for lesson/puzzle content). */
  translated: (key: string, fallback: string) => string
  dir: "ltr" | "rtl"
  isRtl: boolean
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { user, firebaseUser } = useFirebaseAuth()
  const userLocale = (user?.preferences as { locale?: Locale } | undefined)?.locale ?? null
  const stored = getStoredLocale()

  const [locale, setLocaleState] = useState<Locale>(() => {
    return userLocale ?? stored ?? "en"
  })

  useEffect(() => {
    const resolved: Locale = userLocale ?? stored ?? "en"
    setLocaleState(resolved)
  }, [userLocale, stored])

  const setLocale = useCallback(
    (newLocale: Locale) => {
      setLocaleState(newLocale)
      setStoredLocale(newLocale)
      if (firebaseUser && user?.id) {
        updateUserPreferencesInFirestore(user.id, { locale: newLocale }).catch((e) =>
          console.error("[i18n] Failed to persist locale:", e)
        )
      }
    },
    [firebaseUser, user?.id]
  )

  useEffect(() => {
    if (user?.id && stored && !userLocale) {
      updateUserPreferencesInFirestore(user.id, { locale: stored }).catch(() => {})
    }
  }, [user?.id, stored, userLocale])

  useEffect(() => {
    const dir = locale === "he" ? "rtl" : "ltr"
    const lang = locale === "he" ? "he" : "en"
    document.documentElement.setAttribute("dir", dir)
    document.documentElement.setAttribute("lang", lang)
  }, [locale])

  const t = useCallback(
    (key: string) => getMessage(locale, key),
    [locale]
  )

  const translated = useCallback(
    (key: string, fallback: string) => {
      const v = getMessage(locale, key)
      return v === key ? fallback : v
    },
    [locale]
  )

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: typeof t === "function" ? t : ((key: string) => key),
      translated,
      dir: locale === "he" ? "rtl" : "ltr",
      isRtl: locale === "he",
    }),
    [locale, setLocale, t, translated]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

const fallbackT = (key: string) => key

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    return {
      locale: "en",
      setLocale: () => {},
      t: fallbackT,
      translated: (_key: string, fallback: string) => fallback,
      dir: "ltr",
      isRtl: false,
    }
  }
  return {
    ...ctx,
    t: typeof ctx.t === "function" ? ctx.t : fallbackT,
  }
}
